import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import api from "../../api/axiosInstance";
import { API_ENDPOINTS, BASE_URL } from "../../config/apiconfig";
import "../../style/passbook.css";
import CustomerSearch from "../../components/CustomerSearch";
import ConfirmModal from "../../components/common/ConfirmModal";
import AppDropdown from "../../components/AppDropdown";
import { Box, Typography, Pagination } from "@mui/material";
import { Book as PassbookIcon, Person as PersonIcon, Description as DescriptionIcon, PropaneTank as CylinderIcon, CurrencyRupee as RupeeIcon } from "@mui/icons-material";
import { TokenManager } from "../../utils/tokenManager";

const Passbook = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: "", type: "info", onConfirm: null });

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    fromDate: "",
    toDate: "",
  });

  const [productSummary, setProductSummary] = useState([]);
  const [passbookRecords, setPassbookRecords] = useState([]);
  const [showPassbookRecords, setShowPassbookRecords] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0
  });

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value - 1 }));
    fetchPassbookRecords(value - 1);
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, size: newSize, page: 0 }));
    fetchPassbookRecords(0);
  };
  
  const [customerSummary, setCustomerSummary] = useState({
    balance: 0,
    uniqueProducts: 0,
    totalRecords: 0,
    lastUpdated: new Date().toLocaleDateString(),
    status: 'Active'
  });

  // Ref to track current request for race condition protection
  const requestRef = useRef(null);
  
  // Ref to track if we've already loaded from URL parameters
  const hasLoadedFromUrl = useRef(false);

  useEffect(() => {
    // Handle navigation state from Dashboard (passbook button click)
    const state = location.state;
    if (state && state.customerId && !hasLoadedFromUrl.current) {
      const customerData = {
        id: state.customerId,
        name: state.customerName,
        email: state.customerEmail
      };

      setSelectedCustomer(customerData);

      // Clear empty filters
      const emptyFilters = {
        fromDate: "",
        toDate: ""
      };
      setFilters(emptyFilters);
      setAppliedFilters(emptyFilters);

      hasLoadedFromUrl.current = true;

      // Directly fetch passbook summary with customer data
      const fetchSummary = async () => {
        try {
          const response = await api.get(
            API_ENDPOINTS.PASSBOOK.GET_SUMMARY(customerData.id),
            {
              params: {
                fromDate: null,
                toDate: null
              }
            }
          );

          const summaryData = response.data || [];
          setProductSummary(summaryData);

          const totalClosingBalance = summaryData.reduce((sum, item) => sum + (item.closingBalance || 0), 0);
          const uniqueProducts = summaryData.length;

          setCustomerSummary({
            balance: totalClosingBalance,
            uniqueProducts: uniqueProducts,
            totalRecords: summaryData.reduce((sum, item) => sum + (item.totalQuantity || 0), 0),
            lastUpdated: new Date().toLocaleDateString(),
            status: 'Active'
          });
        } catch (error) {
          console.error("Error fetching passbook summary:", error);
        }
      };

      fetchSummary();
      return;
    }

    // Handle URL parameters from billing page or Product Details return
    const customerId = searchParams.get('customerId');
    const fromDate = searchParams.get('fromDate');
    const toDate = searchParams.get('toDate');

    if (customerId && !hasLoadedFromUrl.current) {
      // Fetch customer details
      const fetchCustomerDetails = async () => {
        try {
          const response = await api.get(`customers/getById/${customerId}`);
          const customer = response.data;
          setSelectedCustomer({
            id: customer.id,
            name: customer.name
          });

          // Set date filters if provided
          if (fromDate && toDate) {
            const dateFilters = {
              fromDate: fromDate,
              toDate: toDate
            };
            setFilters(dateFilters);
            setAppliedFilters(dateFilters);
          } else {
            const emptyFilters = {
              fromDate: "",
              toDate: ""
            };
            setFilters(emptyFilters);
            setAppliedFilters(emptyFilters);
          }

          hasLoadedFromUrl.current = true;
        } catch (error) {
          console.error("Error fetching customer details:", error);
        }
      };

      fetchCustomerDetails();
    }
  }, [searchParams, location.state]);

  /* ---------------- FETCH PASSBOOK SUMMARY ---------------- */
  const fetchPassbookSummary = useCallback(async () => {
    // Check if customer is selected
    if (!selectedCustomer) {
      setConfirmModal({ 
        isOpen: true, 
        message: "Please select a customer first", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }
    
    console.log("Fetching passbook summary for customer:", selectedCustomer.id);
    console.log("Applied filters:", appliedFilters);
    
    // Cancel previous request if exists
    if (requestRef.current) {
      requestRef.current.abort();
    }
    
    // Create new AbortController for this request
    const controller = new AbortController();
    requestRef.current = controller;
    
    // Reset passbook records view when applying filter
    setShowPassbookRecords(false);
    setPassbookRecords([]);
    
    try {
      const response = await api.get(
        API_ENDPOINTS.PASSBOOK.GET_SUMMARY(selectedCustomer.id),
        {
          params: {
            fromDate: appliedFilters.fromDate || null,
            toDate: appliedFilters.toDate || null
          },
          signal: controller.signal
        }
      );

      console.log("Passbook summary response:", response.data);

      const summaryData = response.data || [];
      setProductSummary(summaryData);
      
      // Calculate customer summary
      const totalClosingBalance = summaryData.reduce((sum, item) => sum + (item.closingBalance || 0), 0);
      
      setCustomerSummary({
        balance: totalClosingBalance,
        uniqueProducts: summaryData.length,
        totalRecords: summaryData.length,
        lastUpdated: new Date().toLocaleDateString(),
        status: 'Active'
      });

    } catch (error) {
      // Don't show error if request was cancelled
      if (error.name !== 'CanceledError') {
        console.error("Error fetching passbook summary:", error);
        setConfirmModal({ 
          isOpen: true, 
          message: "Failed to load passbook data", 
          type: "error",
          onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
        });
      }
    } finally {
      requestRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id, appliedFilters.fromDate, appliedFilters.toDate]);

  // Auto-fetch when customer is selected (for manual customer selection via search)
  useEffect(() => {
    if (selectedCustomer) {
      // Set flag to true for manual selections
      hasLoadedFromUrl.current = true;
      fetchPassbookSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer?.id, appliedFilters.fromDate, appliedFilters.toDate, fetchPassbookSummary]);

  /* ---------------- FETCH PASSBOOK RECORDS ---------------- */
  const fetchPassbookRecords = async (page = 0) => {
    if (!selectedCustomer) {
      setConfirmModal({ 
        isOpen: true, 
        message: "Please select a customer first", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }
    
    setLoadingRecords(true);
    try {
      const response = await api.get(
        API_ENDPOINTS.PASSBOOK.GET_PASSBOOK,
        {
          params: {
            name: selectedCustomer.name,
            email: selectedCustomer.email,
            page: page,
            size: 10,
            fromDate: appliedFilters.fromDate || null,
            toDate: appliedFilters.toDate || null
          }
        }
      );
      
      const records = response.data?.content || [];
      setPassbookRecords(records);
      setPagination({
        page: response.data?.number || 0,
        size: response.data?.size || 10,
        totalElements: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 0
      });
      setShowPassbookRecords(true);
      
    } catch (error) {
      console.error("Error fetching passbook records:", error);
      setConfirmModal({ 
        isOpen: true, 
        message: "Failed to load passbook records", 
        type: "error",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
    } finally {
      setLoadingRecords(false);
    }
  };

  /* ---------------- HANDLE DOWNLOAD CHALLAN ---------------- */
  const handleDownloadChallan = async (challanId) => {
    try {
      const token = TokenManager.getToken();
      const response = await fetch(`${BASE_URL}/challans/download/${challanId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `challan_${challanId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setConfirmModal({ 
        isOpen: true, 
        message: "Challan downloaded successfully", 
        type: "success",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
    } catch (error) {
      console.error("Error downloading challan:", error);
      setConfirmModal({ 
        isOpen: true, 
        message: "Failed to download challan", 
        type: "error",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------------- HANDLE APPLY FILTER ---------------- */
  const handleApplyFilter = () => {
    if (!selectedCustomer) {
      setConfirmModal({ 
        isOpen: true, 
        message: "Please select a customer first", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }

    // Validate date range
    if (filters.fromDate && filters.toDate && filters.fromDate > filters.toDate) {
      setConfirmModal({ 
        isOpen: true, 
        message: "From Date must be before or equal to To Date", 
        type: "warning",
        onConfirm: () => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })
      });
      return;
    }

    // Update applied filters and trigger API call
    setAppliedFilters({
      fromDate: filters.fromDate,
      toDate: filters.toDate
    });
  };

  /* ---------------- HANDLE RESET ---------------- */
  const handleReset = () => {
    setSelectedCustomer(null);
    setFilters({
      fromDate: "",
      toDate: "",
    });
    setAppliedFilters({
      fromDate: "",
      toDate: "",
    });
    setProductSummary([]);
    setPassbookRecords([]);
    setShowPassbookRecords(false);
    setCustomerSummary({
      balance: 0,
      uniqueProducts: 0,
      totalRecords: 0,
      lastUpdated: "",
      status: "Active",
    });
    hasLoadedFromUrl.current = false;
  };

  /* ---------------- HANDLE VIEW PRODUCT DETAILS ---------------- */
  const handleViewProductDetails = (product) => {
    const productName = product.name || product.productName;
    if (selectedCustomer && productName) {
      // Use URL parameters for better persistence with applied filters
      const params = new URLSearchParams();
      if (appliedFilters.fromDate) params.append('fromDate', appliedFilters.fromDate);
      if (appliedFilters.toDate) params.append('toDate', appliedFilters.toDate);
      
      const queryString = params.toString();
      const url = queryString 
        ? `/dashboard/product-details/${selectedCustomer.id}/${encodeURIComponent(productName)}?${queryString}`
        : `/dashboard/product-details/${selectedCustomer.id}/${encodeURIComponent(productName)}`;
      
      navigate(url);
    }
  };

  return (
    <>
      <div className="passbookContainer">
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: "#4338CA",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 8px 20px rgba(67,56,202,.18)"
            }}
          >
            <PassbookIcon sx={{ fontSize: 26 }} />
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: 38,
                fontWeight: 700,
                lineHeight: 1.1
              }}
            >
              Passbook
            </Typography>

            <Typography
              sx={{
                color: "#64748B",
                fontSize: 15
              }}
            >
              View customer passbook and transaction history
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filter Card */}
      <div className="pb-card">
        <div className="pb-grid">
          <div className="pb-field">
            {/* <label>Customer</label> */}
            <CustomerSearch onSelectCustomer={setSelectedCustomer} selectedCustomer={selectedCustomer} />
          </div>

          <div className="pb-field">
            <label>From Date</label>
            <input
              type="date"
              name="fromDate"
              value={filters.fromDate}
              onChange={handleChange}
            />
          </div>

          <div className="pb-field">
            <label>To Date</label>
            <input
              type="date"
              name="toDate"
              value={filters.toDate}
              onChange={handleChange}
            />
          </div>

          <div className="pb-field pb-field-actions">
            <label>&nbsp;</label>
            <div className="pb-button-group">
              <button className="pb-btn-outline pb-btn-responsive" onClick={handleReset}>
                Clear
              </button>
              <button className="pb-btn-primary pb-btn-responsive" onClick={handleApplyFilter}>
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {!selectedCustomer && (
        <div className="pb-card pb-empty">
          <PersonIcon sx={{ fontSize: 64, color: '#CBD5E1' }} />
          <h3>Please select a customer to view passbook details</h3>
        </div>
      )}

      {/* Customer Summary Card */}
      {selectedCustomer && (
        <div className="pb-card">
          <div className="pb-customer-summary">
            <h3><PersonIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Customer Summary</h3>
            <p className="customer-name">{selectedCustomer.name}</p>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Running Balance</span>
                <span className="summary-value">
                  <RupeeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5, color: (selectedCustomer.runningBalance || 0) < 0 ? '#dc2626' : '#16a34a' }} />
                  <span style={{ color: (selectedCustomer.runningBalance || 0) < 0 ? '#dc2626' : 'inherit' }}>
                    {selectedCustomer.runningBalance || 0}
                  </span>
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Quantity Balance</span>
                <span className="summary-value">{customerSummary.balance}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Unique Products</span>
                <span className="summary-value">{customerSummary.uniqueProducts}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Records</span>
                <span className="summary-value">{customerSummary.totalRecords}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Last Updated</span>
                <span className="summary-value">{customerSummary.lastUpdated}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Status</span>
                <span className="summary-value status-active">{customerSummary.status}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cylinder Details Card */}
      {productSummary.length > 0 && (
        <div className="pb-card">
          <div className="pb-product-summary">
            <h3><CylinderIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Cylinder Details</h3>
            <p className="product-count">Showing {productSummary.length} unique cylinders</p>
            <div className="pb-table-wrapper responsive-table-container">
              <table className="passbook-table responsive-table">
                <thead>
                  <tr>
                    <th>Cylinder Name</th>
                    <th>Delivered Qty</th>
                    <th>Received Qty</th>
                    <th>Balance Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {productSummary.map((product, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ fontWeight: '600', color: '#0f172a' }}>{product.name}</span>
                      </td>
                      <td>
                        <span style={{
                          background: '#dcfce7',
                          color: '#15803d',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.deliveredQty}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: '#dbeafe',
                          color: '#2563eb',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.receivedQty}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: product.balanceQty >= 0 ? '#fef3c7' : '#fee2e2',
                          color: product.balanceQty >= 0 ? '#d97706' : '#dc2626',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.balanceQty}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Product Summary Card */}
      {productSummary.length > 0 && (
        <div className="pb-card">
          <div className="pb-product-summary">
            <h3><DescriptionIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Product Summary</h3>
            <p className="product-count">Showing {productSummary.length} unique products</p>
            <div className="pb-table-wrapper responsive-table-container">
              <table className="passbook-table responsive-table">
                <thead>
                  <tr>
                    <th>Product Name</th>
                    <th>Opening Balance</th>
                    <th>Closing Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {productSummary.map((product, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{ fontWeight: '600', color: '#0f172a' }}>{product.name || product.productName || '—'}</span>
                      </td>
                      <td>
                        <span style={{
                          background: product.openingBalance >= 0 ? '#f1f5f9' : '#fee2e2',
                          color: product.openingBalance >= 0 ? '#475569' : '#dc2626',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.openingBalance ?? 0}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: product.closingBalance >= 0 ? '#f1f5f9' : '#fee2e2',
                          color: product.closingBalance >= 0 ? '#475569' : '#dc2626',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {product.closingBalance ?? 0}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewProductDetails(product)}
                          style={{
                            background: '#4338ca',
                            color: '#fff',
                            border: 'none',
                            padding: '6px 16px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#3730a3'}
                          onMouseOut={(e) => e.target.style.background = '#4338ca'}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* View Passbook Records Button */}
      {productSummary.length > 0 && !showPassbookRecords && (
        <div style={{ padding: '24px 32px', textAlign: 'center' }}>
          <button 
            className="pb-btn-primary pb-btn-responsive"
            onClick={() => fetchPassbookRecords(0)}
            style={{ minWidth: '200px' }}
          >
            View Passbook Records
          </button>
        </div>
      )}

      {/* Passbook Records Card */}
      {showPassbookRecords && (
        <div className="pb-card">
          <div className="pb-product-summary">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3><DescriptionIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 1 }} /> Passbook Records</h3>
              <button
                className="pb-btn-outline pb-btn-responsive"
                onClick={() => setShowPassbookRecords(false)}
              >
                Hide
              </button>
            </div>
            <p className="product-count">{pagination.totalElements} Records • Page {pagination.page + 1} of {pagination.totalPages}</p>
            
            <div className="pb-table-wrapper responsive-table-container">
              <table className="passbook-table responsive-table">
                <thead>
                  <tr>
                    <th>Challan No.</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Qty</th>
                    <th>Deposit Amount</th>
                    <th>Return Amount</th>
                    <th>Created By</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRecords ? (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                        Loading records...
                      </td>
                    </tr>
                  ) : passbookRecords.length === 0 ? (
                    <tr>
                      <td colSpan="9" style={{ textAlign: 'center', padding: '40px' }}>
                        No records found
                      </td>
                    </tr>
                  ) : (
                    passbookRecords.map((record, index) => {
                      const totalQty = record.items?.reduce((sum, item) => 
                        sum + (item.deliveredQty || 0) + (item.receivedQty || 0), 0) || 0;
                      const amount = Number(
                        record.returnedAmount ??
                        record.depositeAmount ??
                        record.depositAmount ??
                        0
                      );
                      const depositAmount = amount > 0 ? amount : 0;
                      const returnAmount = amount < 0 ? Math.abs(amount) : 0;
                      
                      return (
                        <tr key={index}>
                          <td>{record.challanNumber || record.systemChallanNumber}</td>
                          <td>{record.date}</td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              ...(record.challanType === 'DELIVERED' && { background: '#DCFCE7', color: '#15803D' }),
                              ...(record.challanType === 'RECEIVED' && { background: '#DBEAFE', color: '#2563EB' }),
                              ...(record.challanType === 'COMBINED' && { background: '#FEF3C7', color: '#D97706' }),
                            }}>
                              {record.challanType}
                            </span>
                          </td>
                          <td>{record.siteLocation || '-'}</td>
                          <td>{totalQty}</td>
                          <td>{depositAmount > 0 ? <span className="amount-credit">₹ {depositAmount.toFixed(2)}</span> : '-'}</td>
                          <td>{returnAmount > 0 ? <span className="amount-debit">₹ {returnAmount.toFixed(2)}</span> : '-'}</td>
                          <td>{record.createdBy}</td>
                          <td>
                            <button 
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleDownloadChallan(record.id)}
                            >
                              Download Challan
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loadingRecords && pagination.totalPages > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  p: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1 }}>
                  {pagination.totalElements > 0 && (
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                      Showing {pagination.page * pagination.size + 1}–{Math.min((pagination.page + 1) * pagination.size, pagination.totalElements)} of {pagination.totalElements} records
                    </Typography>
                  )}
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.page + 1}
                    onChange={handlePageChange}
                    disabled={loadingRecords}
                    shape="rounded"
                    variant="outlined"
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={0}
                    boundaryCount={1}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        transition: "transform .2s, box-shadow .2s, background-color .2s",
                        borderRadius: "10px",
                        fontWeight: 600,
                        minWidth: 38,
                        height: 38,
                      },
                      "& .MuiPaginationItem-root:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 10px rgba(0,0,0,.15)",
                      },
                      "& .MuiPaginationItem-root:active": {
                        transform: "scale(.95)",
                      },
                      "& .Mui-selected": {
                        backgroundColor: "#4338CA",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#3730a3",
                        },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: {
                      xs: 150,
                      sm: 130,
                    },
                    transition: "all .2s",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500, color: "#475569", mb: 0.75 }}>Rows per page</Typography>
                  <AppDropdown
                    value={pagination.size}
                    options={[
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 25, label: '25' },
                      { value: 50, label: '50' }
                    ]}
                    onChange={handlePageSizeChange}
                    disabled={loadingRecords}
                  />
                </Box>
              </Box>
            )}
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onConfirm={confirmModal.onConfirm || (() => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null }))}
        onCancel={() => setConfirmModal({ isOpen: false, message: "", type: "info", onConfirm: null })}
        title={confirmModal.type === 'error' ? 'Error' : confirmModal.type === 'warning' ? 'Warning' : confirmModal.type === 'success' ? 'Success' : 'Information'}
        message={confirmModal.message}
        confirmText="OK"
        cancelText=""
        type={confirmModal.type === 'error' ? 'danger' : confirmModal.type}
      />
      </div>
    </>
  );
};

export default Passbook;
